/**
 * LotThumbnail — vignette plan 2D pour identifier visuellement un lot.
 *
 * Utilise `lot.normalized_plan_image_url` (PNG normalisé via l'Asset Pipeline).
 * Fallback : icône Layers stylée avec le `lot_number` si pas de plan normalisé.
 *
 * Props:
 *   lot           Lot objet
 *   size          Taille en px (défaut 48)
 *   className     Classes Tailwind additionnelles
 *   showFallback  Si true, affiche un placeholder; sinon retourne null (défaut: true)
 */
import { useState, useEffect } from 'react';
import { Layers } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Cache global blob URL par lot_id pour éviter les re-fetches répétés
// Auto-purgé quand le composant clé est démonté (sentinelle simple).
const _cache = new Map(); // lot_id -> blobUrl

const LotThumbnail = ({ lot, size = 48, className = '', showFallback = true, refreshKey }) => {
  const [src, setSrc] = useState(() => _cache.get(lot?.lot_id) || null);
  const [error, setError] = useState(false);

  useEffect(() => {
    if (!lot?.lot_id || !lot?.normalized_plan_image_url) {
      setSrc(null);
      return;
    }
    // Cache hit
    if (_cache.has(lot.lot_id) && !refreshKey) {
      setSrc(_cache.get(lot.lot_id));
      return;
    }
    let cancelled = false;
    axios
      .get(`${API}/lots/${lot.lot_id}/normalized-plan`, { withCredentials: true, responseType: 'blob' })
      .then((r) => {
        if (cancelled) return;
        const url = URL.createObjectURL(r.data);
        _cache.set(lot.lot_id, url);
        setSrc(url);
        setError(false);
      })
      .catch(() => { if (!cancelled) setError(true); });
    return () => { cancelled = true; };
  }, [lot?.lot_id, lot?.normalized_plan_image_url, refreshKey]);

  const dim = { width: size, height: size };

  if (src && !error) {
    return (
      <img
        src={src}
        alt={lot?.lot_number || 'lot'}
        className={`object-cover rounded-md flex-shrink-0 ${className}`}
        style={{ ...dim, background: '#0d0d0f' }}
        data-testid={`lot-thumb-${lot?.lot_id}`}
      />
    );
  }

  if (!showFallback) return null;

  // Fallback : tile dorée avec le numéro
  return (
    <div
      className={`flex items-center justify-center rounded-md flex-shrink-0 ${className}`}
      style={{
        ...dim,
        background: 'linear-gradient(135deg, rgba(217,160,91,0.12) 0%, rgba(217,160,91,0.04) 100%)',
        border: '1px solid var(--c-border-accent, rgba(217,160,91,0.28))',
        color: 'var(--c-gold, #D9A05B)',
      }}
      data-testid={`lot-thumb-fallback-${lot?.lot_id}`}
      title="Plan non normalisé"
    >
      {size >= 40 && lot?.lot_number ? (
        <span className="text-[10px] font-bold tracking-tight">{lot.lot_number}</span>
      ) : (
        <Layers className="w-3 h-3" />
      )}
    </div>
  );
};

export default LotThumbnail;
